import { defineComponent as _defineComponent } from 'vue'
import { withKeys as _withKeys, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, createBlock as _createBlock, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "timeline-container" }

import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useTimelineMapStore } from "@features/Pages/TimelineMapPage/_js/app/store/timelineMapStore";
import { useVerticalDragScroll } from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useVerticalDragScroll"
import { useWindowListeners } from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useWindowListeners"
import { useTimelinePersistence } from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useTimelinePersistence"
import { useTimelineElementListeners } from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useTimelineElementListeners"
import { useScrollPinning } from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useScrollPinning";
import { readDocumentCssPx } from "@features/Pages/TimelineMapPage/_js/app/services/readDocumentCssPx";
import EpochMM from "../epoch/EpochMM.vue";
import {useTimelineDom} from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useTimelineDom";


export default /*@__PURE__*/_defineComponent({
  __name: 'TimelineMM',
  setup(__props) {

const store = useTimelineMapStore();
const activeIndexRef = ref<number>(-1);

const timelineRef = ref<HTMLElement | null>(null);
const { onPointerDown, onPointerMove, onPointerUp, cancelMomentum } = useVerticalDragScroll(timelineRef, 5);
const { goToEpoch } = useTimelineDom(timelineRef);
const { saveActive } = useTimelinePersistence();
const { smoothScrollTo, readCssPx } = useScrollPinning();

const onScroll = (e: Event) => {};
const onResize = (e: Event) => {};

useWindowListeners({
  onScroll,
  onResize,
});

useTimelineElementListeners(timelineRef, {
  onPointerDown,
  onPointerMove,
  onPointerUp,
  blockWheelAndTouch: true
});

const hasMoreAbove = computed(() => activeIndexRef.value > 2);
const hasMoreBelow = computed(() => activeIndexRef.value < store.epochs.length - 4);

const epochsToRender = computed(() => {
  const epochs = [...store.epochs];
  
  const start = Math.max(0, activeIndexRef.value - 2);
  const end   = Math.min(epochs.length, activeIndexRef.value + 4);

  return epochs.slice(start, end);
});

// Change epoch with arrows - to top or bottom epoch
async function moveUp() {
  if (!store.epochs.length) { return; }
  const firstEpoch = store.epochs[0];

  await goToEpoch(firstEpoch.id);
  const titleEl = document.getElementById(`epoch-title-${firstEpoch.id}`) as HTMLElement | null;
  if (!titleEl) { return; }
  await nextTick();
  await new Promise(r => requestAnimationFrame(r));

  const margin = readCssPx(".site--mm", "--epoch-lock-top", 200);
  const disturbanceHeight = readDocumentCssPx("--disturbance-height", 0);
  const rect = titleEl.getBoundingClientRect();
  const targetTop = window.scrollY + rect.top - margin - disturbanceHeight;

  await smoothScrollTo(targetTop, 700);
  titleEl.focus({ preventScroll: true });
}

async function moveDown() {
  if (!store.epochs.length) { return; }
  const lastEpoch = store.epochs[store.epochs.length - 1];

  await goToEpoch(lastEpoch.id);
  const titleEl = document.getElementById(`epoch-title-${lastEpoch.id}`) as HTMLElement | null;
  if (!titleEl) { return; }
  await nextTick();
  await new Promise(r => requestAnimationFrame(r));

  const margin = readCssPx(".site--mm", "--epoch-lock-top", 200);
  const disturbanceHeight = readDocumentCssPx("--disturbance-height", 0);
  const rect = titleEl.getBoundingClientRect();
  const targetTop = window.scrollY + rect.top - margin - disturbanceHeight;

  await smoothScrollTo(targetTop, 700);
  titleEl.focus({ preventScroll: true });
}

onMounted(async () => {  
  if ('scrollRestoration' in history) { history.scrollRestoration = 'manual'; }
  
  if (store.epochs.length > 0) {
    store.initEpochs(store.epochs, undefined);
    store.ensureActiveEpochVisibility();
    await nextTick();
    await new Promise(r => requestAnimationFrame(r));
    window.scrollTo({ top: 0, behavior: 'auto' });
  }
});

onUnmounted(() => {
  cancelMomentum();
});

watch(
    () => store.activeEpochId,
    (id) => {
      activeIndexRef.value = store.epochs.findIndex((e) => e.id === id);
      store.ensureActiveEpochVisibility();
      if (id != null) { saveActive(id); } 
    },
    { immediate: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      id: "timeline",
      class: "timeline",
      ref_key: "timelineRef",
      ref: timelineRef
    }, [
      (hasMoreAbove.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "timeline__arrow timeline__arrow--up",
            onClick: moveUp,
            onKeydown: [
              _withKeys(moveUp, ["space"]),
              _withKeys(moveUp, ["enter"])
            ],
            tabindex: "0"
          }, null, 32 /* NEED_HYDRATION */))
        : _createCommentVNode("v-if", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(epochsToRender.value, (epoch) => {
        return (_openBlock(), _createBlock(EpochMM, {
          key: epoch.id,
          epoch: epoch,
          index: _unref(store).epochs.findIndex(e => e.id === epoch.id),
          activeIndex: _unref(store).epochs.findIndex(e => e.id === _unref(store).activeEpochId),
          timelineEl: timelineRef.value
        }, null, 8 /* PROPS */, ["epoch", "index", "activeIndex", "timelineEl"]))
      }), 128 /* KEYED_FRAGMENT */)),
      (hasMoreBelow.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: "timeline__arrow timeline__arrow--down",
            onClick: moveDown,
            onKeydown: [
              _withKeys(moveDown, ["space"]),
              _withKeys(moveDown, ["enter"])
            ],
            tabindex: "0"
          }, null, 32 /* NEED_HYDRATION */))
        : _createCommentVNode("v-if", true),
      _cache[0] || (_cache[0] = _createElementVNode("div", { class: "timeline__line" }, null, -1 /* CACHED */))
    ], 512 /* NEED_PATCH */),
    _cache[1] || (_cache[1] = _createElementVNode("div", { class: "timeline__complement-spacing" }, null, -1 /* CACHED */))
  ]))
}
}

})